
import {throwError as observableThrowError, of as observableOf, Observable, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {LoginModel} from '../models/Login.model';
import {TaxplanIQServices} from './TaxplanIQServices';
import {PATH, SOURCE_TYPE} from '../constants/Tpiq.constants';
import {Session} from './Session';
import {UrlService} from './UrlService';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';

declare let _: any;

export function getUserPreferences(org, userId) {
  return `/TaxPlanningServices/users/${userId}/orgs/${org}/preferences`;
}

export function getDocumentsCountUrl(org) {
  return `/TaxPlanningServices/files/recent/count?org_id=${org}&from=0`;
}

export function getPhoneLogCountUrl(org, userId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/phonelog/count`;
}

export function getPasswordSetURL() {
  return `/TaxPlanningServices/users/createPassword`;
}

export function loginUrl(){
  return `/TaxPlanningServices/login`;
}

export function getUserInfoUrl(orgId, userId){
  return `/TaxPlanningServices/org/${orgId}/users/${userId}/users/info`;
}

export function getRefreshTokenUrl(userId){
  return `/TaxPlanningServices/users/${userId}/token/refresh`;
}

export function getForgotPasswordUrl(org){
  return `/TaxPlanningServices/orgs/${org}/user-request/forgotpassword`;
}

export function getResetPasswordUrl(){
  return `/TaxPlanningServices/users/resetpassword`;
}

export function getUpdateCompanyNameUrl(org: string, userId: string) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/company/name`;
}

export function getOrgCompanyUrl(org: string, userId: string) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/company`;
}

export function getUpdateUserProfileUrl(org: string, loggedInUserId: string, targetUserId: string) {
  return `/TaxPlanningServices/org/${org}/users/${loggedInUserId}/users/profile?id=${targetUserId}`;
}

/**
 * The login API reads the firm from the Origin host and needs three host labels
 * (tpiqdevbasic.taxplaniq.com, tpiqdevbasic.imacpro.local). A local dev host such as
 * tpiqdevbasic.localhost has two, so the login sends its subdomain as org. The API still checks
 * that the user belongs to that firm. Other hosts send nothing new.
 */
export function localDevLoginOrg(hostname: string): string | undefined {
  const labels = (hostname || '').toLowerCase().split('.');
  return labels.length === 2 && labels[1] === 'localhost' && labels[0] ? labels[0] : undefined;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService extends TaxplanIQServices{
  userPreferences: any = {};
  // CU-868kcbyxf: single in-flight token refresh shared by every caller
  // (app.component 12-min loop, header, customer-header, posts uploads).
  // Concurrent refreshes used to fire duplicate requests; with rotation
  // semantics coming to the session layer (unified-session epic), a losing
  // duplicate would revoke the winner's token - one shared request instead.
  private refreshInFlight$: Observable<any> | null = null;

  constructor(http: HttpClient) {
     super(http);
  }

  login(loginModel: LoginModel, app?: string): Observable<any> {
    // CU-868kcbzft (CodeRabbit): the login RESPONSE carries the HttpOnly
    // tpiq_session Set-Cookie (P1.4). On a cross-origin XHR the browser only
    // STORES response cookies when the request opts into credentials - the
    // shared create() wrapper never does, so the cookie was silently dropped
    // and the cookie-based rememberMe/session flows could never start.
    // Mirror sessionRefresh(): direct post with withCredentials.
    const url = UrlService.getBaseUrl('JAVA') + loginUrl();
    const org = localDevLoginOrg(window.location.hostname);
    const body = org ? {...loginModel, org} : loginModel;
    return this.http.post(url, body, {withCredentials: true})
      .pipe(catchError(this.handleError));
  }

  forgotPassword(forgotPassword: any, org: any, app?: string): Observable<any> {
    const url = getForgotPasswordUrl(org);
    return this.update(url, forgotPassword, SOURCE_TYPE.JAVA, null, app)
      .pipe(catchError(this.handleError));
  }

  resetPassword(input: any, app?: any): Observable<any> {
    const url = getResetPasswordUrl();
    return this.update(url, input, SOURCE_TYPE.JAVA, null, app).pipe(catchError(this.handleError));
  }

  /**
   * CU-868kcbzft (rememberMe -> shared session): exchange the HttpOnly
   * tpiq_session cookie for a fresh legacy login response. withCredentials
   * carries the cookie (same-site for *.taxplaniq.com hosts); the endpoint
   * is public at the gateway and rotates the session row server-side.
   */
  sessionRefresh(): Observable<any> {
    const url = UrlService.getBaseUrl('JAVA') + '/TaxPlanningServices/session/refresh';
    return this.http.post(url, {}, {withCredentials: true}).pipe(catchError(this.handleError));
  }

  refreshToken(data: any): Observable<any> {
    if (!this.refreshInFlight$) {
      const url = getRefreshTokenUrl(Session.getUser().id);
      this.refreshInFlight$ = this.create(url, data, SOURCE_TYPE.JAVA).pipe(
        catchError(this.handleError),
        // Clear the slot when the underlying call settles (success or error)
        // so the next caller starts a fresh request.
        finalize(() => { this.refreshInFlight$ = null; }),
        shareReplay(1)
      );
    }
    return this.refreshInFlight$;
  }

  getOrgCompany(org: any): Observable<any> {
    const url = getOrgCompanyUrl(org, Session.getUser().id);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateCompanyName(org: string, userId: string, firmName: string): Observable<any> {
    const url = getUpdateCompanyNameUrl(org, userId);
    return this.update(url, { firmName }, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  clearUserPreferences(){
    this.userPreferences = {};
  }

  getUserPreferences(userId: any, org: any): Observable<any>  {
    if (this.userPreferences  && !_.isEmpty(this.userPreferences)) {
      return observableOf(this.userPreferences);
    }
    const url = getUserPreferences(org, userId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(map((res) => {
      this.userPreferences = res;
      return this.userPreferences;
    }), catchError(this.handleError), );
  }

  reLogin(): Observable<any> {
    const url = this.interpolateUrl(PATH.REMEMBER_ME, null);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getUserCompanyInfo(userId: any, org: any, companyId?: any): Observable<any> {
    const url = getUserInfoUrl(org, userId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getUserInfoByToken(params: any): Observable<any> {
    const url = this.interpolateUrl(PATH.USER_INFO_BY_TOKEN_SERVICE, null, {userId: params.userId, org: params.org});
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getUserInfoBySSOToken(params: any): Observable<any> {
    const url = this.interpolateUrl(PATH.USER_INFO_BY_SSO_TOKEN_SERVICE, null, {userId: params.user_id, token: params.ssotoken});
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getDocumentsCount(): Observable<any> {
    const url = getDocumentsCountUrl(Session.getOrg());
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getPhoneLogCount(): Observable<any>  {
    const url = getPhoneLogCountUrl(Session.getOrg(), Session.getUser().id);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  setPassword(token: any, data: any): Observable<any> {
    const url = `${getPasswordSetURL()}?token=${token}`;
    return this.create(url, data, SOURCE_TYPE.JAVA, null).pipe(catchError(this.handleErrorWithResponseCode));
  }

  updatePassword(user: any): Observable<any> {
    const url = `/TaxPlanningServices/orgs/${Session.getOrg()}/user-request/createPassword`;
    return this.update(url, user, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  /**
   * Update user profile fields and (optionally) password in a single call.
   * Backend endpoint: PUT /TaxPlanningServices/org/{orgId}/users/{loggedInUser}/users/profile?id={targetUserId}
   */
  updateUserProfile(user: any): Observable<any> {
    const url = getUpdateUserProfileUrl(Session.getOrg(), Session.getUser().id, user.id);
    return this.update(url, user, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): any {
        return throwError(error.error);
    }

  private handleErrorWithResponseCode(error: HttpErrorResponse): any {
    return throwError(error);
  }
}
